import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import Tw_Cen_MT_Negrita from '../../../../componentes/font/Tw_Cen_MT/TCB_____.TTF';
import Tw_Cen_MT from '../../../../componentes/font/Tw_Cen_MT/TCM_____.TTF';
import NumeroALetras from '../../../../componentes/funciones/NumeroALetras';
import { NegocioConfig } from "../../../../configuracion/Empresa";
import { formatoMoneda, formatoNumero } from "../../../../configuracion/validaciones";

Font.register({ family: 'Tw_Cen_MT', src: Tw_Cen_MT });
Font.register({ family: 'Tw_Cen_MT_Negrita', src: Tw_Cen_MT_Negrita });

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Tw_Cen_MT',
        fontSize: 10,
        paddingTop: 30,
        paddingBottom: 50,
        paddingHorizontal: 40,
        backgroundColor: '#ffffff',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
    },
    companyInfoLeft: { flexGrow: 1 },
    companyName: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 14, color: '#333' },
    companyAddress: { fontSize: 8, color: '#666' },
    logo: { width: 80, height: 80, borderRadius: 10 },

    mainTitle: {
        fontSize: 14,
        fontFamily: 'Tw_Cen_MT_Negrita',
        textAlign: 'left',
        marginTop: 10,
        textTransform: 'uppercase'
    },

    infoGrid: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderBottomColor: '#eee',
        paddingBottom: 10,
        marginBottom: 10,
    },
    infoColumn: { width: '48%' },
    infoLabel: { fontFamily: 'Tw_Cen_MT_Negrita', fontSize: 9, color: '#444' },
    infoText: { fontSize: 9, color: '#555', marginBottom: 3 },

    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#b3b3b3',
        paddingVertical: 4,
        fontFamily: 'Tw_Cen_MT_Negrita',
        color: '#000',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#E0E0E0',
        paddingVertical: 5,
    },
    col1: { width: '10%', textAlign: 'center' },
    col2: { width: '25%', textAlign: 'left', paddingLeft: 5 },
    col3: { width: '20%', textAlign: 'right' },
    col4: { width: '22.5%', textAlign: 'right' },
    col5: { width: '22.5%', textAlign: 'right', paddingRight: 5 },

    datoAnulado: {
        color: 'red',
        textDecoration: 'line-through',
    },

    footerSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 20,
    },
    leftFooter: { width: '60%' },
    rightFooter: { width: '35%' },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 3,
    },
    saldoActualLabel: {
        fontFamily: 'Tw_Cen_MT_Negrita',
        fontSize: 11,
        borderTopWidth: 1,
        paddingTop: 5,
        marginTop: 5
    }
});

const ReporteCredito = ({ compra }) => {
    if (!compra) return null;

    const {
        numeroFactura, proveedor, totalCompra,
        pagoCompras = [], creditocompras = [], facturacompras = [], recibocompras = []
    } = compra;

    const credito = creditocompras[0] || {};
    const montoOriginal = parseFloat(totalCompra) || 0;

    // Lógica de etiqueta dinámica para Compras
    const esFactura = facturacompras && facturacompras.length > 0;
    const esRecibo = recibocompras && recibocompras.length > 0;
    const etiquetaDocumento = esFactura ? "Número de Factura Compra:" : esRecibo ? "Número de Recibo Compra:" : "N° Documento:";

    // Cálculo de historial de saldos
    let saldoAcumulado = montoOriginal;
    const historialConSaldos = pagoCompras.map((pc) => {
        const p = pc.pago;
        const esAnulado = p.estado === 'Anulada';
        const montoPago = p.pago;

        const saldoAnterior = saldoAcumulado;
        if (!esAnulado) {
            saldoAcumulado -= montoPago;
        }
        const saldoNuevo = saldoAcumulado;

        // Detectar método de pago para compras
        let metodo = 'Efectivo';
        if (p.pagotarjeta?.length > 0) metodo = 'Tarjeta';
        else if (p.pagotransferencia?.length > 0) metodo = 'Transferencia';

        return {
            fecha: p.fecha,
            monto: montoPago,
            estado: p.estado,
            saldoAnterior: saldoAnterior,
            saldoActual: esAnulado ? saldoAnterior : saldoNuevo,
            metodo: metodo
        };
    });

    const totalPagado = historialConSaldos
        .filter(p => p.estado !== 'Anulada')
        .reduce((acc, curr) => acc + curr.monto, 0);

    // Saldo real actual de esta compra
    const saldoPendiente = montoOriginal - totalPagado;

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.companyInfoLeft}>
                        <Text style={styles.companyName}>{NegocioConfig.nombre}</Text>
                        <Text style={styles.companyAddress}>{NegocioConfig.direccion}</Text>
                        <Text style={styles.companyAddress}>RTN: {NegocioConfig.rtn}</Text>
                        <Text style={styles.mainTitle}>ESTADO DE CUENTA - COMPRA N° {formatoNumero(numeroFactura)}</Text>
                    </View>
                    {NegocioConfig.logo && <Image style={styles.logo} src={NegocioConfig.logo} />}
                </View>

                {/* Info Grid */}
                <View style={styles.infoGrid}>
                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de Compra:</Text>
                        <Text style={styles.infoText}>{moment(credito.fecha).format('DD/MM/YYYY HH:mm')}</Text>

                        <Text style={styles.infoLabel}>Nombre del Proveedor:</Text>
                        <Text style={styles.infoText}>{proveedor?.nombre || "N/A"}</Text>

                        <Text style={styles.infoLabel}>{etiquetaDocumento}</Text>
                        <Text style={styles.infoText}>{formatoNumero(numeroFactura)}</Text>
                    </View>

                    <View style={styles.infoColumn}>
                        <Text style={styles.infoLabel}>Fecha de vencimiento:</Text>
                        <Text style={styles.infoText}>{moment(credito.fechaVencimiento).format('DD/MM/YYYY')}</Text>

                        <Text style={styles.infoLabel}>RTN Proveedor:</Text>
                        <Text style={styles.infoText}>{proveedor?.rtn || "N/A"}</Text>

                        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                            <View>
                                <Text style={styles.infoLabel}>Total Compra:</Text>
                                <Text style={styles.infoText}>{formatoMoneda(montoOriginal)}</Text>
                            </View>
                            <View>
                                <Text style={styles.infoLabel}>Saldo Pendiente:</Text>
                                <Text style={[styles.infoText, { fontFamily: 'Tw_Cen_MT_Negrita', color: 'red' }]}>
                                    {formatoMoneda(saldoPendiente)}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* Tabla de Abonos */}
                <View style={styles.tableHeader}>
                    <Text style={styles.col1}>N pago</Text>
                    <Text style={styles.col2}>Fecha Pago</Text>
                    <Text style={styles.col3}>Monto</Text>
                    <Text style={styles.col4}>Saldo Anterior</Text>
                    <Text style={styles.col5}>Saldo Actual</Text>
                </View>

                {historialConSaldos.map((item, index) => {
                    const esAnulado = item.estado === 'Anulada';
                    return (
                        <View key={index} style={styles.tableRow}>
                            <Text style={[styles.col1, esAnulado && styles.datoAnulado]}>{index + 1}</Text>
                            <Text style={[styles.col2, esAnulado && styles.datoAnulado]}>
                                {moment(item.fecha).format('DD/MM/YYYY HH:mm')}
                            </Text>
                            <Text style={[styles.col3, esAnulado && styles.datoAnulado]}>
                                {formatoMoneda(item.monto)}
                            </Text>
                            <Text style={[styles.col4, esAnulado && styles.datoAnulado]}>
                                {formatoMoneda(item.saldoAnterior)}
                            </Text>
                            <Text style={[styles.col5, esAnulado && styles.datoAnulado]}>
                                {formatoMoneda(item.saldoActual)}
                            </Text>
                        </View>
                    );
                })}

                {/* Totales y Letras */}
                <View style={styles.footerSection}>
                    <View style={styles.leftFooter}>
                        <Text style={styles.infoLabel}>Monto en letras (Saldo Pendiente):</Text>
                        <Text style={[styles.infoText, { marginBottom: 15 }]}>{NumeroALetras(saldoPendiente)}</Text>

                        <Text style={styles.infoLabel}>Formas de pago utilizadas:</Text>
                        <Text style={styles.infoText}>
                            {Array.from(new Set(historialConSaldos.map(h => h.metodo))).join(', ')}
                        </Text>
                    </View>

                    <View style={styles.rightFooter}>
                        <View style={styles.totalRow}>
                            <Text style={styles.infoLabel}>Saldo Inicial:</Text>
                            <Text>{formatoMoneda(montoOriginal)}</Text>
                        </View>
                        <View style={styles.totalRow}>
                            <Text style={styles.infoLabel}>Total Abonado:</Text>
                            <Text>{formatoMoneda(totalPagado)}</Text>
                        </View>
                        <View style={[styles.totalRow, styles.saldoActualLabel]}>
                            <Text>SALDO ACTUAL:</Text>
                            <Text style={{ color: '#D32F2F' }}>{formatoMoneda(saldoPendiente)}</Text>
                        </View>
                    </View>
                </View>

                <Text style={{ textAlign: 'center', fontSize: 7, color: '#999', marginTop: 40, borderTopWidth: 0.5, paddingTop: 10 }}>
                    Documento de control interno para el seguimiento de cuentas por pagar a proveedores.
                </Text>
            </Page>
        </Document>
    );
};

export default ReporteCredito;